import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../common';
import { Heading, Text, Typography } from '../common/Typography';
import { Card } from '../common/Card';
import ClubBanner from '../clubs/ClubBanner';
import ClubLogo from '../clubs/ClubLogo';
import { ClubPersonalInsights, getClubRoleBadgeClass, getClubRoleLabel } from '../clubs/ClubPersonalInsights';
import { homeSectionHeading, homeElevatedInset } from './homeDashboardStyles';
import { interactiveBleedInsetX } from '../../constants/interactiveSafeInsets';

const LEFT_ACCENTS = [
  'border-l-primary',
  'border-l-success',
  'border-l-info',
];

/**
 * HomeYourClubs — club snapshot strip with coloured left accents (dashboard polish).
 */
export function HomeYourClubs({ clubs, loading }) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="mb-10">
        <Heading level={2} className={`${homeSectionHeading} mb-5`}>Your clubs</Heading>
        <div className="text-center py-8 rounded-2xl border border-border-medium bg-surface-subtle/60">
          <Typography variant="muted">Loading clubs...</Typography>
        </div>
      </div>
    );
  }

  const recentClubs = clubs?.clubs || [];
  const hasAnyClubs = recentClubs.length > 0;

  if (!hasAnyClubs) {
    return (
      <div className="mb-10">
        <Heading level={2} className={`${homeSectionHeading} mb-5`}>Your clubs</Heading>
        <Card variant="default" className={`${homeElevatedInset} text-center py-10 px-6`}>
          <div className="text-4xl mb-3">🏢</div>
          <Text className="text-text-secondary mb-6">
            You haven&apos;t joined any clubs yet
          </Text>
          <Button
            onClick={() => navigate('/clubs')}
            variant="primary"
          >
            Discover Clubs
          </Button>
        </Card>
      </div>
    );
  }

  const getRoleBadgeColor = getClubRoleBadgeClass;
  const getRoleLabel = getClubRoleLabel;

  let accentIndex = 0;

  const renderClubCard = (club) => {
    const leftAccent = LEFT_ACCENTS[accentIndex % LEFT_ACCENTS.length];
    accentIndex += 1;

    return (
      <Card
        key={club.id}
        variant="default"
        className={`relative flex flex-col self-stretch border-l-[3px] overflow-hidden snap-start shrink-0 p-0 transition-all duration-200 hover:shadow-brand-lg hover:-translate-y-0.5 cursor-pointer ${homeElevatedInset} ${leftAccent} max-sm:w-[min(100%,18.5rem)] max-sm:shrink-0 sm:min-w-[260px] sm:max-w-[320px]`}
        onClick={() => navigate(`/clubs/${club.id}`)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate(`/clubs/${club.id}`);
          }
        }}
      >
        <span className="pointer-events-none absolute bottom-2 right-3 z-[1] text-3xl opacity-[0.06] select-none" aria-hidden>
          🏛️
        </span>
        <ClubBanner
          bannerImageUrl={club.bannerImageUrl ?? club.banner_image_url ?? null}
          clubName=""
          location=""
          variant="card"
          imageHeightClass="h-[5.25rem] sm:h-32"
          className="rounded-none"
        />


        <div className="flex flex-1 flex-col px-4 pt-3 pb-4 min-h-0">
          <div className="flex min-h-[2.75rem] shrink-0 items-start justify-between gap-2 mb-1 pr-1">
            <div className="flex items-center gap-2 min-w-0">
              {(club.logoImageUrl || club.logo_image_url) && (
                <ClubLogo
                  logoImageUrl={club.logoImageUrl ?? club.logo_image_url ?? null}
                  clubName={club.name}
                  size="sm"
                  className="shrink-0"
                />
              )}
              <Typography className="font-bold text-text-primary leading-snug line-clamp-2">{club.name}</Typography>
            </div>
            <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-[0.6875rem] font-semibold ${getRoleBadgeColor(club.role)}`}>
              {getRoleLabel(club.role)}
            </span>
          </div>

          <ClubPersonalInsights
            clubId={club.id}
            clubWinRatePct={club.clubWinRatePct}
            leaderboardTeaser={club.leaderboardTeaser}
            layout="strip"
            className="mt-auto flex flex-1 flex-col min-h-0"
          />
        </div>
      </Card>
    );
  };

  const rowClass = `flex gap-4 items-stretch pb-2 pt-1 scroll-smooth touch-pan-x max-sm:overflow-x-auto max-sm:snap-x max-sm:snap-mandatory sm:flex-row sm:overflow-x-auto sm:gap-4 ${interactiveBleedInsetX} [scrollbar-gutter:stable_both-edges]`;

  accentIndex = 0;
  const clubCards = recentClubs.map((club) => renderClubCard(club));

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-1 gap-3">
        <Heading level={2} className={homeSectionHeading}>Your clubs</Heading>
        <Button
          onClick={() => navigate('/clubs')}
          variant="text"
          className="text-sm shrink-0 font-semibold text-primary"
        >
          View all
        </Button>
      </div>
      <Typography variant="caption" className="text-text-secondary block mb-5">
        Recently played
      </Typography>

      <div className={rowClass} role="list">
        {clubCards}
      </div>

      {recentClubs.length > 1 ? (
        <Typography variant="caption" className="mt-3 text-center text-text-muted px-2 max-sm:block sm:hidden">
          Swipe sideways for more clubs · or tap <span className="font-semibold text-text-secondary">View all</span>
        </Typography>
      ) : null}
    </div>
  );
}
